/*
 * Typen/Defaults EINGEFROREN durch AP-1 — nicht umbenennen/umsortieren ohne
 * Änderungsantrag. Persistenz (`loadSettings`/`saveSettings`) ist AP-6.
 * Ersetzt `config.txt` neben der EXE (LoadSettings/SaveSettings in
 * CanMonitorApp.cpp). Beschreibung: CONTRACTS.md §6.
 */

import type { CanBitrate } from '../can/ICanAdapter';
import { DEFAULT_BITRATE, DEFAULT_DATA_BITRATE } from '../can/ICanAdapter';
import type { AppTheme, UiScalePercent } from '../ui/theme';

/** Kanonische Theme-Typen leben in `src/ui/theme.ts` (AP-0) — hier nur weitergereicht. */
export type { AppTheme, UiScalePercent };
/** Alias, damit Vertragstexte eindeutig bleiben. */
export type ThemeId = AppTheme;

/** Schlüsselwerte in `config.txt` des Originals — für Import/Export. */
export const THEME_CONFIG_NAME: Readonly<Record<ThemeId, string>> = {
  dark: 'Dark',
  bright: 'Bright',
  'light-blue': 'LightBlue',
  'light-green': 'LightGreen',
};

/** `Help > Fade Speed`: 0 = statisch (kein Ausblenden), sonst Dauer in ms.
 *  Reihenfolge = Menüeinträge „Static", „1 (Fastest)" … „5 (Slowest)". */
export const RX_FADE_STEPS_MS = [0, 150, 250, 400, 600, 900] as const;

export type RxColorMode = 'whole-row' | 'dots' | 'no-colors';

export type ViewMode = 'static' | 'trace';

/**
 * `signalDetailMode`: `windows` oeffnet beim Doppelklick ein Detailfenster,
 * `list` klappt die Signalliste eingerueckt unter der Zeile auf (Vorgabe).
 * Kein Menueintrag mehr -- nur ueber die gespeicherten Settings umstellbar.
 */
export type SignalDetailMode = 'windows' | 'list';

/** Spiegelt `enum EmbeddedViewKind` (ohne `None`). */
export type ViewKind =
  | 'dbc-browser'
  | 'eds-browser'
  | 'dm1'
  | 'node-scan'
  | 'signal-plot'
  | 'tx-message'
  | 'address-claim'
  | 'isobus-event-log'
  | 'bus-stats'
  | 'engine-simulator';

/** Anzahl Spalten der Frametabelle (`headers[]` in CreateControls). */
export const TABLE_COLUMN_COUNT = 14;

export const TABLE_COLUMN_TITLES = [
  'n',
  'ID Hex',
  'DLC',
  'B1',
  'B2',
  'B3',
  'B4',
  'B5',
  'B6',
  'B7',
  'B8',
  'Name',
  'Description',
  'dt(ms)',
] as const;

/** Felder mit `config.txt`-Entsprechung sind unten mit ihrem Schlüssel benannt. */
export interface Settings {
  /** config.txt: Theme */
  theme: ThemeId;
  /** config.txt: UiScalePercent */
  uiScalePercent: UiScalePercent;
  /** config.txt: RxFadeMs */
  rxFadeMs: number;
  /** config.txt: RxColorMode */
  rxColorMode: RxColorMode;
  /** config.txt: SingleWindowMode */
  singleWindowMode: boolean;
  /** config.txt: SignalDetailMode */
  signalDetailMode: SignalDetailMode;
  /** config.txt: SimulationMode */
  simulationMode: boolean;
  /** config.txt: ViewMode */
  viewMode: ViewMode;
  /** config.txt: FilterShow11Bit */
  filterShow11Bit: boolean;
  /** config.txt: FilterShow29Bit */
  filterShow29Bit: boolean;

  // --- Nur PWA (im Original nicht persistiert) ---
  bitrate: CanBitrate;
  dataBitrate: number;
  /** Unabhängige Dekodierschalter (`isobusEnabled_` / `canopenEnabled_`). */
  isobusEnabled: boolean;
  canopenEnabled: boolean;
  /** Anteil der oberen Hälfte im Single-Window-Mode (`mainSplitterRatio_`). */
  splitterRatio: number;
  /** Pixelbreiten der 14 Spalten; leer = Vorgabebreiten berechnen. */
  columnWidths: number[];
  /** Zuletzt genutzter Port für den Auto-Reconnect beim Start. */
  lastPort: { usbVendorId: number; usbProductId: number } | null;
}

export const defaultSettings: Settings = {
  theme: 'dark',
  uiScalePercent: 100,
  rxFadeMs: 400,
  rxColorMode: 'no-colors',
  // Abweichung vom Original (dort `singleWindowMode_ = false`): im Browser ist
  // ein einziges Fenster mit angedockten Ansichten das erwartete Verhalten.
  singleWindowMode: true,
  signalDetailMode: 'list',
  simulationMode: false,
  viewMode: 'static',
  filterShow11Bit: true,
  filterShow29Bit: true,
  bitrate: DEFAULT_BITRATE,
  dataBitrate: DEFAULT_DATA_BITRATE,
  isobusEnabled: false,
  canopenEnabled: false,
  splitterRatio: 0.6,
  columnWidths: [],
  lastPort: null,
};

/** localStorage-Schlüssel; Suffix bei inkompatibler Schemaänderung erhöhen. */
export const SETTINGS_STORAGE_KEY = 'can-mon.settings.v1';

/** IndexedDB: Datenbankname und Objektspeicher (DBC/EDS/Traces/Lizenz). */
export const IDB_NAME = 'can-mon';
export const IDB_STORES = {
  files: 'files',
  license: 'license',
} as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Lädt `Settings` aus `localStorage`. Unbekannte Schlüssel werden ignoriert,
 * fehlende aus `defaultSettings` ergänzt (Vorwärtskompatibilität, CONTRACTS.md §6).
 * Ein defektes/fremdes JSON-Objekt führt nie zum Absturz, nur zu den Defaults.
 */
export function loadSettings(): Settings {
  const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
  if (raw === null) return { ...defaultSettings };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...defaultSettings };
  }
  if (!isPlainObject(parsed)) return { ...defaultSettings };

  const merged = { ...defaultSettings };
  for (const key of Object.keys(defaultSettings) as (keyof Settings)[]) {
    if (key in parsed) (merged as Record<string, unknown>)[key] = parsed[key];
  }
  return merged;
}

/** Schreibt das komplette Settings-Objekt (JSON) nach `localStorage`. */
export function saveSettings(settings: Settings): void {
  localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}
